import { clip, type Bones, type Clip } from '../clip.ts';
import { TAU, envelope, inCubic, inOut, inQuad, outBack, outCubic, outExpo, outQuad, phase, settle, span, type Ease } from '../curves.ts';
import { apply, timeline, type Beat, type KeyPose } from '../pose.ts';

/*
 * Troll de turba: idle and walk (loops), attack (a raking two-handed swipe that starts cocked and lands at
 * ~0.13 s, when the simulation deals it), hit, stun (loop), die (it folds onto its knees and slumps into a heap,
 * like a bank of bog giving way) and `regen` (it shudders while its wound seams flare and knit). Everything is
 * loose and wet: the long arms swing like ropes, the hands trail the forearms, the mane of moss sloshes behind.
 *
 * Conventions as in human.ts: limbs hang along -Y, so a negative X swings an arm, hand or leg forward; +X on a
 * shin bends the knee back, -X on a forearm bends the elbow forward; +X on a trunk leans it forward; +X on the jaw
 * opens it; +X on the mane swings it back; +Y turns a trunk to its right (+X); +Z on the right arm lifts it out to
 * the side (-Z on the left). The `mend*` bones are the wound seams: their scale is how hard the knitting light burns.
 */

type TB =
  | 'root' | 'hips' | 'chest' | 'head' | 'jaw' | 'mane'
  | 'armL' | 'foreL' | 'handL' | 'armR' | 'foreR' | 'handR'
  | 'legL' | 'shinL' | 'legR' | 'shinR' | 'mend' | 'mendA' | 'mendL';
type TP = KeyPose<TB>;

/** A damped impulse at `t0` (0 before it). */
const kick = (t: number, t0: number, freq = 2.5, damp = 7): number => (t < t0 ? 0 : settle(t - t0, freq, damp));

/** Whole-figure squash from impulses: a landing flattens it a hair and it springs back. */
const thud = (t: number, hits: readonly [number, number][]): [number, number, number] => {
  let k = 0;
  for (const [t0, a] of hits) k += a * Math.max(0, kick(t, t0, 3, 9));
  return [1 + 0.6 * k, 1 - k, 1 + 0.6 * k];
};

/** A deterministic shiver (two sines at integer multiples of `f`). */
const shiver = (t: number, f: number): number => 0.6 * Math.sin(TAU * f * t) + 0.4 * Math.sin(TAU * f * 2 * t + 1.3);

/** Adds the stance to every beat (so a timeline's beats only spell what changes from it). */
const over = (st: TP, beats: readonly (readonly [number, TP, Ease?])[]): Beat<TB>[] =>
  beats.map(([t, p, e]) => (e ? [t, { ...st, ...p }, e] : [t, { ...st, ...p }]) as Beat<TB>);

/** All three seams at once (scale of the knitting light). */
const seams = (b: Bones<TB>, k: number, kA = k, kL = k): void => {
  b.mend.scale(k);
  b.mendA.scale(kA);
  b.mendL.scale(kL);
};

/** Stooped and loose: knees soft, the long arms hanging a little forward, the head lifted to peer down its nose. */
const T_ST: TP = {
  root: [0, 0, 0, 0, -0.02, 0],
  hips: [0.04, 0, 0],
  chest: [0.05, 0, 0],
  head: [-0.14, 0, 0],
  armL: [-0.1, 0, -0.04],
  foreL: [-0.08, 0, 0],
  handL: [-0.08, 0, 0],
  armR: [-0.08, 0, 0.04],
  foreR: [-0.1, 0, 0],
  handR: [-0.1, 0, 0],
  legL: [-0.1, 0, -0.03],
  shinL: [0.16, 0, 0],
  legR: [-0.08, 0, 0.03],
  shinR: [0.14, 0, 0],
};

/** The hanging bits trail the body: hands after forearms, the mane after the trunk, the jaw after the head. */
const LAG: Partial<Record<TB, number>> = { head: 0.03, jaw: 0.04, mane: 0.07, foreL: 0.02, foreR: 0.02, handL: 0.045, handR: 0.045 };

/**
 * It sways on its long legs, breathing wet and slow; the head turns and sniffs (little lifts of the long nose),
 * a hand flexes its claws, and the seams glow and fade with the breath.
 */
const trollIdle = (): Clip<TB> =>
  clip<TB>('idle', 3.2, true, (b, t) => {
    const p = phase(t, 3.2) * TAU;
    apply(b, T_ST);
    const br = Math.sin(p * 2);
    const sniff = Math.max(0, Math.sin(p * 4 + 0.6)) ** 6 * Math.max(0, Math.sin(p + 2));
    b.root.pos(0.015 * Math.sin(p), -0.01 * (1 + br), 0).rot(0, 0, 0.02 * Math.sin(p));
    b.hips.rot(0, -0.03 * Math.sin(p), -0.015 * Math.sin(p));
    b.chest.rot(-0.04 * br, 0.06 * Math.sin(p + 0.3), 0.025 * Math.sin(p + 0.4)).scale(1 + 0.01 * br);
    b.head.rot(0.04 * br - 0.12 * sniff, 0.35 * Math.sin(p + 1.8), 0.06 * Math.sin(p + 1));
    b.jaw.rot(0.1 + 0.08 * Math.max(0, -Math.sin(p * 2 - 0.4)));
    b.mane.rot(0.04 * Math.sin(p * 2 - 0.9), 0, -0.04 * Math.sin(p - 0.6));
    // The arms swing like ropes from the sway; the right hand flexes its claws once a loop.
    b.armL.rot(0.05 * Math.sin(p - 0.5), 0, -0.03 * Math.sin(p - 0.5));
    b.armR.rot(0.05 * Math.sin(p - 0.7), 0, -0.03 * Math.sin(p - 0.7));
    b.foreL.rot(-0.04 * Math.sin(p - 0.9));
    b.handL.rot(0.06 * Math.sin(p - 1.2));
    const flex = Math.max(0, Math.sin(p + 0.8)) ** 4;
    b.foreR.rot(-0.15 * flex);
    b.handR.rot(0.08 * Math.sin(p - 1.4) + 0.35 * flex);
    b.shinL.rot(0.03 * Math.sin(p));
    b.shinR.rot(-0.03 * Math.sin(p));
    seams(b, 1 + 0.12 * br, 1 + 0.12 * Math.sin(p * 2 - 0.7), 1 + 0.12 * Math.sin(p * 2 - 1.4));
  }, (t) => {
    const br = Math.sin(phase(t, 3.2) * TAU * 2);
    return [1 + 0.01 * br, 1 - 0.008 * br, 1 + 0.01 * br];
  });

/**
 * The lope: long loose strides, knees lifted high, the body bobbing low on each footfall; the long arms swing far
 * against the legs, the knuckles nearly brushing the ground at the bottom of each swing, and the mane sloshes.
 */
const trollWalk = (dur = 1.3): Clip<TB> =>
  clip<TB>('walk', dur, true, (b, t) => {
    const th = phase(t, dur) * TAU;
    const sn = Math.sin(th), cs = Math.cos(th);
    const land = Math.max(0, -Math.cos(2 * th - 0.3)) ** 3;
    apply(b, T_ST, 0.8);
    const A = 0.5;
    b.legL.rot(-A * sn - 0.08, 0, -0.03);
    b.legR.rot(A * sn - 0.08, 0, 0.03);
    b.shinL.rot(1.1 * Math.max(0, cs) ** 1.4 + 0.3 * Math.max(0, -sn) + 0.14 + 0.1 * land);
    b.shinR.rot(1.1 * Math.max(0, -cs) ** 1.4 + 0.3 * Math.max(0, sn) + 0.14 + 0.1 * land);
    b.root.pos(0.035 * Math.sin(th - 0.4), 0.04 * Math.cos(2 * th) - 0.02 - 0.035 * land, 0).rot(0.02 * land, 0.07 * sn, -0.05 * Math.sin(th - 0.4));
    b.hips.rot(0.03 * land, 0.14 * sn, 0.03 * Math.sin(th - 0.5));
    b.chest.rot(0.1 + 0.05 * Math.cos(2 * th - 0.7) + 0.05 * land, -0.2 * sn, 0.05 * Math.sin(th - 0.9));
    // The head stays level, pecking forward a little on each footfall.
    b.head.rot(-0.08 - 0.06 * Math.cos(2 * th - 1.0) - 0.05 * land, 0.14 * sn, -0.04 * Math.sin(th - 1.1));
    b.jaw.rot(0.12 + 0.08 * land);
    b.mane.rot(0.12 + 0.1 * Math.cos(2 * th - 1.5), 0.08 * Math.sin(th - 1.3), 0.1 * Math.sin(th - 1.4));
    // Arms swing wide against the legs; on the forward swing the elbow bends and the hand trails behind.
    const sL = Math.sin(th - 0.25), sR = -sL;
    b.armL.rot(0.55 * sL, 0, -0.06 - 0.04 * land);
    b.armR.rot(0.55 * sR, 0, 0.06 + 0.04 * land);
    b.foreL.rot(-0.35 * Math.max(0, -Math.sin(th - 0.6)) - 0.05);
    b.foreR.rot(-0.35 * Math.max(0, Math.sin(th - 0.6)) - 0.05);
    b.handL.rot(0.35 * Math.sin(th - 0.9));
    b.handR.rot(-0.35 * Math.sin(th - 0.9));
    seams(b, 1 + 0.08 * land);
  }, (t) => {
    const k = Math.max(0, -Math.cos(phase(t, 1.3) * TAU * 2 - 0.3)) ** 3;
    return [1 + 0.025 * k, 1 - 0.03 * k, 1 + 0.025 * k];
  });

/**
 * The rake: it starts reared back with both arms flung up over its head and the claws cocked, wrenches both hands
 * down through the target in one raking pull (it lands at 0.13 s), the trunk folding after them and the body
 * lunging a step, carries the claws through past its knees, then hauls itself back up to its stoop.
 */
const T_COCK: TP = {
  root: [0, 0, 0, 0, 0.01, -0.06],
  hips: [-0.08, 0, 0],
  chest: [-0.38, 0.1, 0],
  head: [-0.3, 0.05, 0],
  jaw: [0.6, 0, 0],
  mane: [0.35, 0, 0],
  armL: [-2.55, 0, -0.5],
  foreL: [-0.85, 0, 0],
  handL: [0.55, 0, 0],
  armR: [-2.65, 0, 0.45],
  foreR: [-0.95, 0, 0],
  handR: [0.6, 0, 0],
  legL: [-0.18, 0, -0.04],
  shinL: [0.28, 0, 0],
  legR: [0.08, 0, 0.04],
  shinR: [0.2, 0, 0],
};
const T_RAKE: TP = {
  root: [0, 0, 0, 0, -0.08, 0.14],
  hips: [0.18, 0, 0],
  chest: [0.36, -0.05, 0],
  head: [-0.05, 0, 0],
  jaw: [0.45, 0, 0],
  mane: [-0.2, 0, 0],
  armL: [-0.75, 0, 0.12],
  foreL: [-0.25, 0, 0],
  handL: [-0.55, 0, 0],
  armR: [-0.7, 0, -0.12],
  foreR: [-0.25, 0, 0],
  handR: [-0.6, 0, 0],
  legL: [-0.5, 0, -0.05],
  shinL: [0.5, 0, 0],
  legR: [0.2, 0, 0.05],
  shinR: [0.35, 0, 0],
};
const T_THROUGH: TP = {
  ...T_RAKE,
  root: [0, 0, 0, 0, -0.1, 0.16],
  chest: [0.48, -0.05, 0],
  head: [0.05, 0, 0],
  jaw: [0.3, 0, 0],
  mane: [-0.3, 0, 0],
  armL: [0.3, 0, -0.2],
  foreL: [-0.35, 0, 0],
  handL: [-0.85, 0, 0],
  armR: [0.35, 0, 0.2],
  foreR: [-0.35, 0, 0],
  handR: [-0.9, 0, 0],
};

const trollAttack = (): Clip<TB> =>
  clip<TB>('attack', 0.85, false, (b, t) => {
    timeline(b, t, over(T_ST, [
      [0, T_COCK],
      [0.05, { ...T_COCK, chest: [-0.42, 0.12, 0], armL: [-2.65, 0, -0.52], armR: [-2.75, 0, 0.47] }, outQuad],
      [0.13, T_RAKE, inCubic],
      [0.26, T_THROUGH, outCubic],
      [0.4, { ...T_THROUGH, chest: [0.44, -0.05, 0], armL: [0.2, 0, -0.15], armR: [0.25, 0, 0.15] }],
      [0.85, {}, inOut],
    ]), { ...LAG, handL: 0.025, handR: 0.025 });
    // The jolt of the claws catching, up the arms and through the trunk.
    const k = kick(t, 0.13, 5, 10);
    b.chest.rot(0.04 * k);
    b.armL.rot(0.1 * k);
    b.armR.rot(0.1 * k);
  }, (t) => thud(t, [[0.13, 0.035], [0.26, 0.015]]));

/** It jerks back from the blow, the head snapping up and the arms flung loose; the mane slaps after. */
const trollHit = (): Clip<TB> =>
  clip<TB>('hit', 0.4, false, (b, t) => {
    apply(b, T_ST);
    const k = settle(t, 2.6, 8) * span(t, 0, 0.03, outQuad);
    const a = Math.abs(k);
    b.chest.rot(-0.28 * k, 0.12 * k, 0.06 * k);
    b.hips.rot(-0.08 * k);
    b.head.rot(-0.45 * k, -0.2 * k);
    b.jaw.rot(0.35 * a);
    b.armL.rot(0.25 * k, 0, -0.18 * a);
    b.armR.rot(0.2 * k, 0, 0.16 * a);
    b.root.pos(0, -0.015 * a, -0.05 * k);
    b.shinL.rot(0.1 * a);
    b.shinR.rot(0.12 * a);
    const lag = settle(t - 0.05, 2.2, 6) * span(t, 0.02, 0.07);
    b.mane.rot(0.4 * lag, 0, 0.1 * lag);
    b.handL.rot(0.45 * lag);
    b.handR.rot(0.4 * lag);
    // The wounds flinch dark, then come back.
    seams(b, 1 - 0.4 * span(t, 0, 0.04) * (1 - span(t, 0.15, 0.4)));
  }, (t) => {
    const k = Math.max(0, settle(t, 3, 9));
    return [1 + 0.04 * k, 1 - 0.05 * k, 1 + 0.04 * k];
  });

/** Dazed: it reels on its long legs, the head lolling round on the neck, both arms swinging slack as ropes. */
const trollStun = (): Clip<TB> =>
  clip<TB>('stun', 2, true, (b, t) => {
    const p = phase(t, 2) * TAU;
    apply(b, T_ST, 0.6);
    b.root.rot(0.04 * Math.sin(p), 0, 0.08 * Math.cos(p)).pos(0.04 * Math.cos(p), -0.04, 0);
    b.hips.rot(0.06, 0, -0.03 * Math.cos(p));
    b.chest.rot(0.3 + 0.06 * Math.sin(p * 2), 0.1 * Math.sin(p), -0.1 * Math.cos(p));
    b.head.rot(0.35 + 0.2 * Math.sin(p + 0.8), 0.4 * Math.cos(p + 0.8), 0.3 * Math.sin(p + 0.8));
    b.jaw.rot(0.45 + 0.08 * Math.sin(p * 2));
    b.mane.rot(-0.1 + 0.12 * Math.sin(p + 1.6), 0, 0.15 * Math.cos(p + 1.6));
    b.armL.rot(0.2 * Math.sin(p + 1.4), 0, 0.1 * Math.cos(p + 1.4));
    b.armR.rot(0.2 * Math.sin(p + 1.2), 0, 0.1 * Math.cos(p + 1.2));
    b.foreL.rot(0.05);
    b.foreR.rot(0.05);
    b.handL.rot(0.3 * Math.sin(p + 2.0), 0, 0.15 * Math.cos(p + 2.2));
    b.handR.rot(0.3 * Math.sin(p + 1.8), 0, 0.15 * Math.cos(p + 2.0));
    b.shinL.rot(0.18);
    b.shinR.rot(0.18 + 0.1 * Math.sin(p));
    seams(b, 0.7 + 0.1 * Math.sin(p * 2));
  });

/**
 * Struck down: it rears with a gargle, arms flung up; the long legs fold and it drops onto its knees; it sways,
 * the seams guttering out, and then the whole thing slumps forward and sinks into a heap of peat and moss, the
 * arms spilling out in front of it like roots.
 */
const trollDie = (): Clip<TB> =>
  clip<TB>('die', 2.4, false, (b, t) => {
    const knees: TP = { legL: [0.1, 0, -0.15], shinL: [1.75, 0, 0], legR: [0.12, 0, 0.15], shinR: [1.8, 0, 0] };
    const kneel: TP = {
      ...knees, root: [0, 0, 0, 0, -0.42, -0.02], hips: [0.2, 0, 0], chest: [0.35, 0.1, 0.05], head: [0.35, 0.2, 0.1], jaw: [0.5, 0, 0], mane: [0.1, 0, 0],
      armL: [-0.15, 0, -0.2], foreL: [-0.2, 0, 0], handL: [0.4, 0, 0], armR: [-0.1, 0, 0.25], foreR: [-0.15, 0, 0], handR: [0.45, 0, 0],
      mend: [0, 0, 0, 0, 0, 0, 0.6], mendA: [0, 0, 0, 0, 0, 0, 0.6], mendL: [0, 0, 0, 0, 0, 0, 0.6],
    };
    const heap: TP = {
      legL: [0.15, 0, -0.35], shinL: [1.95, 0, 0], legR: [0.2, 0, 0.35], shinR: [2.0, 0, 0],
      root: [0, 0, 0.04, 0, -0.5, 0.08], hips: [0.55, 0, 0], chest: [0.95, 0.15, 0.1], head: [0.55, 0.5, 0.25], jaw: [0.55, 0, 0], mane: [-0.5, 0, 0.2],
      armL: [-1.35, 0, -0.6], foreL: [0.1, 0, 0], handL: [-0.6, 0, 0], armR: [-1.1, 0, 0.75], foreR: [-0.15, 0, 0], handR: [-0.5, 0, 0],
      mend: [0, 0, 0, 0, 0, 0, 0.05], mendA: [0, 0, 0, 0, 0, 0, 0.05], mendL: [0, 0, 0, 0, 0, 0, 0.05],
    };
    timeline(b, t, [
      [0, T_ST],
      [0.22, { ...T_ST, root: [0, 0, 0, 0, 0.01, -0.05], hips: [-0.12, 0, 0], chest: [-0.45, 0.1, 0.05], head: [-0.7, 0.15, 0], jaw: [0.95, 0, 0], mane: [0.5, 0, 0], armL: [-0.55, 0, -0.45], foreL: [-0.7, 0, 0], handL: [0.5, 0, 0], armR: [-0.45, 0, 0.5], foreR: [-0.6, 0, 0], handR: [0.5, 0, 0], legL: [0.06, 0, -0.04], legR: [-0.12, 0, 0.04], mend: [0, 0, 0, 0, 0, 0, 1.6], mendA: [0, 0, 0, 0, 0, 0, 1.5], mendL: [0, 0, 0, 0, 0, 0, 1.5] }, outExpo],
      [0.5, { ...T_ST, root: [0, 0, 0, 0, -0.1, -0.03], chest: [0.12, 0.05, 0.05], head: [0.2, 0.1, 0], jaw: [0.5, 0, 0], armL: [0.05, 0, -0.15], armR: [0.05, 0, 0.12], legL: [-0.3, 0, -0.08], shinL: [0.6, 0, 0], legR: [-0.3, 0, 0.08], shinR: [0.6, 0, 0], mend: [0, 0, 0, 0, 0, 0, 1.1] }, inOut],
      [0.78, kneel, inQuad],
      [0.9, { ...kneel, root: [0, 0, 0, 0, -0.4, -0.02], chest: [0.45, 0.1, 0.05], head: [0.5, 0.2, 0.1] }, outQuad],
      [1.3, { ...kneel, chest: [0.25, 0.12, 0.08], head: [0.25, 0.3, 0.15], mend: [0, 0, 0, 0, 0, 0, 0.35] }, inOut],
      [1.95, heap, inQuad],
      [2.4, { ...heap, root: [0, 0, 0.04, 0, -0.52, 0.08], chest: [1.0, 0.15, 0.1] }, outBack],
    ], LAG);
    // It shudders as it rears.
    const tr = envelope(t, 0.18, 0.24, 0.42, 0.5) * shiver(t, 11);
    b.chest.rot(0.03 * tr, 0.02 * tr);
    b.head.rot(0.05 * tr);
  }, (t) => {
    // The knees hitting the ground, then the heap slumping and spreading as the bog gives way.
    const k = t > 0.78 ? 0.05 * Math.max(0, kick(t, 0.78, 3, 9)) : 0;
    const melt = span(t, 1.75, 2.4, inOut);
    return [1 + 0.6 * k + 0.16 * melt, 1 - k - 0.3 * melt, 1 + 0.6 * k + 0.16 * melt];
  });

/**
 * Regenerating: it hunches in on itself, then rears up with its chest thrown out and arms hung wide, shuddering
 * while the seams flare and the flesh knits; then a wet shake of the head and it sinks back into its stoop.
 */
const trollRegen = (): Clip<TB> =>
  clip<TB>('regen', 1.0, false, (b, t) => {
    const hunch: TP = {
      root: [0, 0, 0, 0, -0.07, 0], hips: [0.1, 0, 0], chest: [0.3, 0, 0], head: [0.3, 0, 0], jaw: [0.2, 0, 0], mane: [0.1, 0, 0],
      armL: [-0.15, 0, 0.12], foreL: [-0.35, 0, 0], handL: [0.35, 0, 0], armR: [-0.12, 0, -0.12], foreR: [-0.35, 0, 0], handR: [0.35, 0, 0],
      legL: [-0.25, 0, -0.04], shinL: [0.42, 0, 0], legR: [-0.22, 0, 0.04], shinR: [0.4, 0, 0],
    };
    const rear: TP = {
      root: [0, 0, 0, 0, 0.02, -0.02], hips: [-0.06, 0, 0], chest: [-0.3, 0, 0], head: [-0.5, 0, 0], jaw: [0.7, 0, 0], mane: [0.35, 0, 0],
      armL: [0.15, 0, -0.55], foreL: [-0.45, 0, 0], handL: [0.6, 0, 0], armR: [0.15, 0, 0.55], foreR: [-0.45, 0, 0], handR: [0.6, 0, 0],
      legL: [-0.06, 0, -0.06], shinL: [0.1, 0, 0], legR: [-0.04, 0, 0.06], shinR: [0.1, 0, 0],
    };
    timeline(b, t, over(T_ST, [
      [0, {}],
      [0.16, hunch, outCubic],
      [0.32, rear, outBack],
      [0.64, { ...rear, chest: [-0.26, 0, 0], head: [-0.44, 0, 0], armL: [0.12, 0, -0.5], armR: [0.12, 0, 0.5] }],
      [1.0, {}, inOut],
    ]), LAG);
    // The shudder while it knits, and the wet shake at the end.
    const sh = envelope(t, 0.28, 0.34, 0.6, 0.68) * shiver(t, 13);
    b.chest.rot(0.03 * sh, 0.04 * sh, 0.03 * sh);
    b.head.rot(0.05 * sh, 0.06 * sh);
    b.root.pos(0.012 * sh, 0, 0);
    const shake = envelope(t, 0.64, 0.7, 0.84, 0.95) * Math.sin(TAU * 7 * t);
    b.head.rot(0, 0.25 * shake, 0.1 * shake);
    b.mane.rot(0, 0.2 * shake, 0.2 * shake);
    // The seams flare up past full and pulse as they knit, one after another, then sink back.
    const f = (d: number): number => 1 + 1.3 * envelope(t, 0.14 + d, 0.34 + d, 0.62 + d, 0.94, outCubic) * (1 + 0.18 * Math.sin(TAU * 6 * t + d * 9));
    seams(b, f(0), f(0.05), f(0.1));
  }, (t) => {
    // Swells as it knits.
    const k = envelope(t, 0.28, 0.4, 0.6, 0.8, outCubic);
    const s = envelope(t, 0.28, 0.34, 0.6, 0.68) * Math.sin(TAU * 13 * t);
    return [1 + 0.035 * k + 0.01 * s, 1 + 0.02 * k, 1 + 0.035 * k + 0.01 * s];
  }, 'Regenerar');

export const trollClips = (): Clip[] => [trollIdle(), trollWalk(), trollAttack(), trollHit(), trollStun(), trollDie(), trollRegen()] as Clip[];

/** Clips by library kind (see ../rigs.ts). */
/** The Furrows' prowler and La Orilla's Drowned share the troll's rig (models/prowler.ts, models/drowned.ts), so they move the same stooped, dragging way. */
export const CLIPS: Record<string, () => Clip[]> = { troll: trollClips, prowler: trollClips, drowned: trollClips };
